import React, {useEffect, useReducer, useState} from 'react';
import PersonSlider from "@/Pages/Manage/PersonSlider";
import DeletePerson from "@/Components/Modals/DeletePerson";
import PeopleHeader from "@/Pages/Manage/PeopleHeader";
import {useDebounce} from "@/Utilities/useDebounce";
import FilterSlider from "@/Pages/Manage/FilterSlider";
import Spinner from "@/Components/Spinner";
import i18n from "i18next";
import PeopleTableHead from "@/Pages/Manage/PeopleTableHead";
import PeopleTableBody from "@/Pages/Manage/PeopleTableBody";
import bffApiUrlService from "@/Utilities/BffApiUrlService";

export default function People({auth}) {
    function reducer(state, action) {
        switch (action.type) {
            case "refresh":
                return {...state, refresh: action.payload.refresh}
            case "personSliderReady":
                return {...state, personSliderReady: action.payload.personSliderReady}
            case "showPersonSlider":
                return {...state, showPersonSlider: action.payload.showPersonSlider}
            case "person":
                return {...state, person: action.payload.person}
            case "filterSliderReady":
                return {...state, filterSliderReady: action.payload.filterSliderReady}
            case "showFilterSlider":
                return {...state, showFilterSlider: action.payload.showFilterSlider}
            case "deletePersonConfirmation":
                return {...state, deletePersonConfirmation: action.payload.deletePersonConfirmation}
            case "searchValue":
                return {...state, searchValue: action.payload.searchValue, page: 0}
            case "people":
                return {...state, users: action.payload.users, pagination: action.payload.pagination}
            case "filters":
                return {...state, filters: action.payload.filters, page: 0}
            case "page":
                return {...state, page: action.payload.page}
            case "pageSize" :
                return {...state, pageSize: action.payload.pageSize, page: 0}
            case "isLoading":
                return {...state, isLoading: action.payload.isLoading}
            case "sortDirectionAsc":
                return {...state, sortDirection: "asc"}
            case "sortDirectionDesc":
                return {...state, sortDirection: "desc"}
            case "columnSort":
                return {...state, columnSort: action.payload.columnSort}
            case "sortingToggle":
                return {
                    ...state,
                    sortingToggle: state.columnSort === action.payload.sortingToggle ? state.sortingToggle + 1 : 1
                }
            default:
                return state;
        }
    }

    const [state, dispatch] = useReducer(reducer, {
        refresh: false,
        personSliderReady: false,
        showPersonSlider: false,
        person: null,
        filterSliderReady: false,
        showFilterSlider: false,
        deletePersonConfirmation: {
            isOpen: false,
            setIsOpen: setIsDeletePersonConfirmationOpen,
            person: null
        },
        searchValue: '',
        isLoading: true,
        people: null,
        filters: [],
        sortDirection: 'asc',
        columnSort: '',
        sortingToggle: 1,
        page: 1,
        pageSize: 10
    });

    const [statusItems, setStatusItems] = useState([
        {id: i18n.t("filter-slider.authenticated"), label: i18n.t("filter-slider.authenticated"), checked: false},
        {id: i18n.t("filter-slider.invitation-sent"), label: i18n.t("filter-slider.invitation-sent"), checked: false},
        {id: i18n.t("filter-slider.access-disabled"), label: i18n.t("filter-slider.access-disabled"), checked: false},
    ]);
    const [accountTypeItems, setAccountTypeItems] = useState([
        {id: i18n.t("filter-slider.universal-admin"), label: i18n.t("filter-slider.universal-admin"), checked: false},
        {
            id: i18n.t("filter-slider.not-universal-admin"),
            label: i18n.t("filter-slider.not-universal-admin"),
            checked: false
        },
    ]);

    const [moduleAccessItems, setModuleAccessItems] = useState([]);

    useEffect(() => {
        axios.get(bffApiUrlService.getOrganizationFilterableModulesEndpoint())
            .then(response => {
                const modules = response.data.modules.map(module => ({
                    id: "module-" + module.slug, 
                    label: module.name, 
                    checked: false, 
                }));
                setModuleAccessItems(modules);
            })
            .catch(error => {
                console.error("Error fetching modules:", error);
            });
    }, []);

    const delayedSearch = useDebounce(
        (search, statusFilters, accountFilters, moduleFilters, page, pageSize, column, sortDirection) =>
            axios.get(bffApiUrlService.getOrganizationUsersListEndpoint(), {
                params: {
                    search: search,
                    status: JSON.stringify(statusFilters),
                    account: JSON.stringify(accountFilters),
                    modules: JSON.stringify(moduleFilters),
                    page: page,
                    pageSize: pageSize,
                    columnName: (column === "name" ? "first_name" : column === "status" ? "organization_user.status->current" : column),
                    sortDirection: sortDirection
                }
            }).then(response => {
                dispatch({type: "people", payload: {users: response.data.users, pagination: response.data.pagination}});
            }).then(() => {
                dispatch({type: "isLoading", payload: {isLoading: false}});
            }), null, false, [state.searchValue, state.filters, state.columnSort, state.sortDirection, state.page, state.pageSize]);

    useEffect(() => {
        let statusFilters = statusItems.filter(item => item.checked === true)
            .map(checkedItem => checkedItem.label);
        let accountFilters = accountTypeItems.filter(item => item.checked === true)
            .map(checkedItem => checkedItem.label);
        let moduleFilters = moduleAccessItems.filter(item => item.checked === true)
            .map(checkedItem => checkedItem.label);

        dispatch({type: "isLoading", payload: {isLoading: true}});
        if (state.searchValue.length === 0 && !state.refresh) {
            delayedSearch.cancel();
        }
        delayedSearch(state.searchValue,
            statusFilters,
            accountFilters,
            moduleFilters,
            state.page,
            state.pageSize,
            state.columnSort,
            state.sortDirection);
        dispatch({type: "refresh", payload: {isLoading: false}});

    }, [state.searchValue, state.filters, state.columnSort, state.sortDirection, state.page, state.pageSize, state.refresh])

    return (
        <div>
            <div className="people">
                <PeopleHeader addPersonSlider={addPersonSlider}
                              addFilterSlider={addFilterSlider}
                              statusItems={statusItems}
                              setStatusItems={setStatusItems}
                              accountTypeItems={accountTypeItems}
                              setAccountTypeItems={setAccountTypeItems}
                              moduleAccessItems={moduleAccessItems}
                              setModuleAccessItems={setModuleAccessItems}
                              state={state}
                              dispatch={dispatch}/>
                {state.isLoading ? <div className="row"><Spinner/></div> :
                    <div className="people-container">
                        <div className="row">
                            <div className="col-12 table-responsive grid-container">
                                <table data-testid="people-table" className="table table-borderless people-table">
                                    <PeopleTableHead state={state} dispatch={dispatch}/>
                                    <PeopleTableBody state={state}
                                                     dispatch={dispatch}
                                                     setIsDeletePersonConfirmationOpen={setIsDeletePersonConfirmationOpen}/>
                                </table>
                            </div>
                            <div className="mt-3 p-0">

                            </div>
                        </div>
                        {state.deletePersonConfirmation.isOpen && <DeletePerson
                            person={state.deletePersonConfirmation.person}
                            isOpen={state.deletePersonConfirmation.isOpen}
                            setIsOpen={state.deletePersonConfirmation.setIsOpen}
                        ></DeletePerson>}
                    </div>
                }
            </div>
            {state.personSliderReady &&
                <PersonSlider
                    dispatch={dispatch}
                    organization={auth.current_organization}
                    person={state.person}
                />
            }
            {state.filterSliderReady &&
                <FilterSlider
                    state={state}
                    dispatch={dispatch}
                    statusItems={statusItems}
                    setStatusItems={setStatusItems}
                    accountTypeItems={accountTypeItems}
                    setAccountTypeItems={setAccountTypeItems}
                    moduleAccessItems={moduleAccessItems}
                    setModuleAccessItems={setModuleAccessItems}
                />
            }
        </div>
    );

    function addFilterSlider() {
        dispatch({type: "filterSliderReady", payload: {filterSliderReady: true}});
        dispatch({type: "showFilterSlider", payload: {showFilterSlider: true}});
    }

    function addPersonSlider() {
        dispatch({type: "person", payload: {person: null}});
        dispatch({type: "personSliderReady", payload: {personSliderReady: true}});
        dispatch({type: "showPersonSlider", payload: {showPersonSlider: true}});
    }

    function setIsDeletePersonConfirmationOpen(open) {
        dispatch({
            type: "deletePersonConfirmation", payload: {
                deletePersonConfirmation: {
                    isOpen: open,
                    person: null,
                    setIsOpen: setIsDeletePersonConfirmationOpen
                }
            }
        });
        dispatch({type: "refresh", payload: {refresh: true}});
    }
}
